import type { BrandSuggestion, Workspace } from "@brandwall/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError, post } from "../api";
import { Steps } from "../components/Steps";

export function NewBrand() {
  const [website, setWebsite] = useState("");
  const [found, setFound] = useState<BrandSuggestion | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const read = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const b = await post<BrandSuggestion>("/v1/brand/read", { website });
      setFound(b);
      setName(b.name ?? new URL(b.website).hostname.replace(/^www\./, "").split(".")[0] ?? "");
    } catch (err) {
      // Reading the site is a nice-to-have. If it fails, carry on with the name only.
      setError(
        err instanceof ApiError
          ? `${err.message} You can add your details by hand.`
          : "We couldn't read that site. You can add your details by hand.",
      );
      setFound({ website, name: null, logoUrl: null, colors: [], fonts: [] });
      const host =
        website
          .replace(/^https?:\/\//, "")
          .replace(/^www\./, "")
          .split(/[./]/)[0] ?? "";
      setName(host ? host[0]!.toUpperCase() + host.slice(1) : "");
    } finally {
      setBusy(false);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ws = await post<Workspace>("/v1/workspaces", {
        name,
        website: found?.website ?? website,
        brand: {
          logoUrl: found?.logoUrl ?? null,
          colors: found?.colors ?? [],
          fonts: found?.fonts ?? [],
        },
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate(`/w/${ws.id}/connect`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <div className="onb">
      <div className="box">
        <img className="logo" src="/logo.svg" alt="Brandwall" />
        <Steps at={1} />
        {!found ? (
          <form className="card stack" onSubmit={read}>
            <div>
              <h1>Your website</h1>
              <p className="muted">
                We'll pick up your logo, colours and fonts, so your wall looks like the rest of your
                site.
              </p>
            </div>
            <label className="field">
              Website
              <input
                className="input"
                required
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="fjellbu.dk"
              />
            </label>
            <button type="submit" className="btn block" disabled={busy}>
              {busy ? "Reading your site…" : "Continue"}
            </button>
          </form>
        ) : (
          <form className="card stack" onSubmit={create}>
            <div>
              <h1>
                {found.logoUrl || found.colors.length
                  ? "We found your brand"
                  : "Tell us about your brand"}
              </h1>
              <p className="muted">From {found.website}. You can change all of this later.</p>
            </div>
            {error && <div className="error">{error}</div>}
            {found.logoUrl && (
              <div className="row">
                <img
                  src={found.logoUrl}
                  alt="Your logo"
                  style={{ maxHeight: 48, maxWidth: 200, objectFit: "contain" }}
                />
              </div>
            )}
            <label className="field">
              Brand name
              <input
                className="input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
            </label>
            {found.colors.length > 0 && (
              <div className="stack" style={{ gap: 6 }}>
                <span className="small" style={{ fontWeight: 600 }}>
                  Colours
                </span>
                <div className="row">
                  {found.colors.map((c) => (
                    <span key={c} className="swatch" style={{ background: c }} title={c} />
                  ))}
                </div>
              </div>
            )}
            {found.fonts.length > 0 && (
              <p className="small">
                <b>Fonts:</b> {found.fonts.join(", ")}
              </p>
            )}
            <button type="submit" className="btn block" disabled={busy}>
              Looks right
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
